import React from 'react';
import { env_url } from './endpoints';
import {
	getPlayerImageURL,
	getPlayerLink,
	formatNumberWithDecimal,
	calculatePercentage,
	formatGameTeamDetails,
	formatDivisionRanksNumericValue,
	getPlayerLinkWithNumberAndPosition,
	getPlayerWithoutLinkWithNumberAndPosition,
	overrideTeamTricode,
	buildGamePageUrl,
} from './shared_helpers.js';
import update from 'immutability-helper';
import { preserveQueryParams } from './browser_helpers';
import { getTeamLogo as getSharedTeamLogo } from './shared_helpers.js';
export const NHLsilhouette = `${env_url}/player-silhouette-hockey-cut.png`;

export const NHLLogo = 'https://images.rogersdigitalmedia.com/www.sportsnet.ca/team_logos/200x200/hockey/nhl/nhl.png';

export const imgErrNHL = ev => {
	ev.target.src = NHLsilhouette;
};

/*
 * formatPeriodName: This function returns the text label of a period based on it's numerical value
 *
 * Input: period - integer corresponding to a period
 * Input: game_type - string describing type of game. e.g. Preseason
 *
 * Output: period - string of text representing the period in plain english
 */
export const formatPeriodName = (period, gameType, league, timestamp) => {
	if (period === 1) {
		return '1st';
	} else if (period === 2) {
		return '2nd';
	} else if (period === 3) {
		return '3rd';
	} else if (period === 4) {
		return 'OT';
	} else {
		if (gameType === 'Regular Season' || gameType === 'Preseason' || gameType === 'Pre-Season') {
			return 'SO';
		} else {
			if (league === 'whc' && timestamp >= 1715350800 && timestamp < 1716747600) {
				return 'SO';
			} else {
				// Post season game
				return period - 3 + 'OT';
			}
		}
	}
};

/*
 * labelPeriods: This function adds text labels to the periods array
 *
 * Input: periods - array of period objects
 * Input: game_type - string describing type of game. e.g. Preseason
 *
 * Output: periods - array: the same array originally input, but with an added "label" paramter in each period object.
 */
export const labelPeriods = (periods, game_type, ighClips = []) => {
	let periodCopy = JSON.parse(JSON.stringify(periods));

	if (game_type === 'Regular Season' || game_type === 'Preseason') {
		periodCopy.forEach(function(period, i) {
			if (i < 3) {
				periodCopy[i].label = i + 1;
			} else if (i === 3) {
				periodCopy[i].label = 'OT';
			} else {
				periodCopy[i].label = 'SO';
			}
		});
	} else {
		//Post season game
		periodCopy.forEach(function(period, i) {
			if (i < 3) {
				periodCopy[i].label = i + 1;
			} else if (i === 3) {
				periodCopy[i].label = 'OT';
			} else {
				periodCopy[i].label = i - 2 + 'OT';
			}
		});
	}

	if (ighClips && ighClips.length !== 0) {
		periodCopy.forEach((period, p) => {
			let type = period.goals ? 'goals' : period.shootouts ? 'shootouts' : '';

			if (period.hasOwnProperty(type) && period[type].length !== 0) {
				period[type].forEach((item, g) => {
					if (item.player_id) {
						const igh_length = ighClips.length;
						for (let i = 0; i < igh_length; i++) {
							const ighClip = ighClips[i];
							if (ighClip.title && ighClip.keywords && ighClip.players && ighClip.players[0]) {
								const regExpForParenthesesValue = /\(([^)]+)\)$/;
								const match = regExpForParenthesesValue.exec(ighClip.title);
								const time_from_title = match[1];

								if (time_from_title) {
									const colon_split = time_from_title.split(':');
									let igh_goal_minutes = null;
									let igh_goal_seconds = null;
									let is_igh_shootout = null;

									if (type === 'goals' && !isNaN(item.minutes) && !isNaN(item.seconds)) {
										igh_goal_minutes = colon_split[0] && parseInt(colon_split[0], 10);

										igh_goal_seconds = colon_split[1].split('/')[0] && parseInt(colon_split[1].split('/')[0], 10);
									} else if (type === 'shootouts') {
										is_igh_shootout = colon_split[1].split('/')[1] && colon_split[1].split('/')[1] === 'SO';
									}

									// Include a buffer of 3 seconds
									let match_with_buffer = false;
									let wsc_total_seconds = item.minutes * 60 + item.seconds;
									let igh_total_seconds = igh_goal_minutes * 60 + igh_goal_seconds;
									if (Math.abs(wsc_total_seconds - igh_total_seconds) <= 3) {
										match_with_buffer = true;
									}

									if (
										(type === 'goals' &&
											match_with_buffer &&
											ighClip.keywords === 'goal' &&
											ighClip.players[0][0].includes(item.player_id)) ||
										(type === 'shootouts' &&
											is_igh_shootout &&
											((ighClip.keywords === 'goal' && item.is_goal) ||
												(ighClip.keywords === 'shot' && !item.is_goal)) &&
											ighClip.players[0][0].includes(item.player_id))
									) {
										periodCopy[p][type][g]['brightcove_video'] = {
											id: ighClip.brightcove_id || null,
											title: ighClip.title || '',
											thumbnail_url: ighClip.thumbnail_url || '',
										};
									}
								}
							}
						}
					}
				});
			}
		});
	}

	return periodCopy;
};

export const nhlTeamNameIDArrayMap = new Map([
	['441862de-0f24-11e2-8525-18a905767e44', 'anaheim-ducks'],
	['44153da1-0f24-11e2-8525-18a905767e44', 'arizona-coyotes'],
	['4416ba1a-0f24-11e2-8525-18a905767e44', 'boston-bruins'],
	['4416d559-0f24-11e2-8525-18a905767e44', 'buffalo-sabres'],
	['44182a9d-0f24-11e2-8525-18a905767e44', 'carolina-hurricanes'],
	['44159241-0f24-11e2-8525-18a905767e44', 'calgary-flames'],
	['4416272f-0f24-11e2-8525-18a905767e44', 'chicago-blackhawks'],
	['44167db4-0f24-11e2-8525-18a905767e44', 'columbus-blue-jackets'],
	['4415ce44-0f24-11e2-8525-18a905767e44', 'colorado-avalanche'],
	['44157522-0f24-11e2-8525-18a905767e44', 'dallas-stars'],
	['44169bb9-0f24-11e2-8525-18a905767e44', 'detroit-red-wings'],
	['4415ea6c-0f24-11e2-8525-18a905767e44', 'edmonton-oilers'],
	['4418464d-0f24-11e2-8525-18a905767e44', 'florida-panthers'],
	['44151f7a-0f24-11e2-8525-18a905767e44', 'los-angeles-kings'],
	['4416091c-0f24-11e2-8525-18a905767e44', 'minnesota-wild'],
	['441713b7-0f24-11e2-8525-18a905767e44', 'montreal-canadiens'],
	['44174b0c-0f24-11e2-8525-18a905767e44', 'new-jersey-devils'],
	['441643b7-0f24-11e2-8525-18a905767e44', 'nashville-predators'],
	['441766b9-0f24-11e2-8525-18a905767e44', 'new-york-islanders'],
	['441781b9-0f24-11e2-8525-18a905767e44', 'new-york-rangers'],
	['4416f5e2-0f24-11e2-8525-18a905767e44', 'ottawa-senators'],
	['44179d47-0f24-11e2-8525-18a905767e44', 'philadelphia-flyers'],
	['4417b7d7-0f24-11e2-8525-18a905767e44', 'pittsburgh-penguins'],
	['44155909-0f24-11e2-8525-18a905767e44', 'san-jose-sharks'],
	['1fb48e65-9688-4084-8868-02173525c3e1', 'seattle-kraken'],
	[('441660ea-0f24-11e2-8525-18a905767e44', 'st-louis-blues')],
	['4417d3cb-0f24-11e2-8525-18a905767e44', 'tampa-bay-lightning'],
	['441730a9-0f24-11e2-8525-18a905767e44', 'toronto-maple-leafs'],
	['715a1dba-4e9f-4158-8346-3473b6e3557f', 'utah-hockey-club'],
	['4415b0a7-0f24-11e2-8525-18a905767e44', 'vancouver-canucks'],
	['42376e1c-6da8-461e-9443-cfcf0a9fcc4d', 'vegas-golden-knights'],
	['4417eede-0f24-11e2-8525-18a905767e44', 'washington-capitals'],
	['44180e55-0f24-11e2-8525-18a905767e44', 'winnipeg-jets'],
]);

const eventNameMap = new Map([
	['awardedgoal', 'Goal'],
	['challenge', 'Challenge'],
	['emptynetgoal', 'Goal'],
	['endperiod', 'End Period'],
	['endshootoutperiod', 'End Shootout'],
	['evenstrength', 'Even Strength'],
	['faceoff', 'Faceoff'],
	['gamesetup', 'Game Setup'],
	['giveaway', 'Giveaway'],
	['goal', 'Goal'],
	['goaliechange', 'Goalie Change'],
	['hit', 'Hit'],
	['owngoal', 'Goal'],
	['penalty', 'Penalty'],
	['penaltygoal', 'Goal'],
	['penaltyshotmissed', 'Penalty Shot: Miss'],
	['penaltyshotsaved', 'Penalty Shot: Save'],
	['powerplay', 'Power Play'],
	['shootoutgoal', 'Goal'],
	['shootoutshotmissed', 'Shootout Shot Missed'],
	['shootoutshotsaved', 'Shootout: Save'],
	['shotmissed', 'Miss'],
	['shotsaved', 'Shot'],
	['startshootoutperiod', 'Shootout'],
	['stoppage', 'Stoppage'],
	['substitutions', 'Substitutions'],
	['takeaway', 'Takeaway'],
	['teamtimeout', 'Team Timeout'],
	['tvtimeout', 'TV Timeout'],
]);

export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam, gameType) {
	const ghostPlays = [
		{ id: 1, isGhost: true },
		{ id: 2, isGhost: true },
		{ id: 3, isGhost: true },
		{ id: 4, isGhost: true },
		{ id: 5, isGhost: true },
		{ id: 6, isGhost: true },
		{ id: 7, isGhost: true },
		{ id: 8, isGhost: true },
		{ id: 9, isGhost: true },
	];

	if (!lastNineEvents) return ghostPlays;

	const massagedPlays = lastNineEvents.map(play => {
		const formattedPlayDescription = play.description;

		return {
			id: play.id,
			title: setPlayTitle(play, gameType),
			color: getPlayColor(play.event),
			teamColor: getTeamColor(play.team_id, homeTeam, visitingTeam),
			bodyText: shortenPlayerNames(formattedPlayDescription, play.players),
			teamLogo: play.team_id !== 0 ? getTeamLogo(play.team_id, homeTeam, visitingTeam) : NHLLogo,
			showPlayerHeadshot: play.player_id !== 0 ? true : false,
			playerHeadshot: play.player_id ? getPlayerImageURL(play.player_id, 'xs', 'nhl') : null,
			playerHeadshotError: imgErrNHL,
		};
	});

	return [...massagedPlays, ...ghostPlays].slice(0, 9);
}

function setPlayTitle(play, gameType) {
	let formattedEvent = eventNameMap.get(play.event);
	let formattedPeriod = formatPeriodName(play.period, gameType);

	if (formattedPeriod === 'SO') {
		formattedEvent = `${formattedEvent}`;
	} else {
		formattedEvent = `${formattedEvent} - ${play.time} / ${formattedPeriod}`;
	}

	return formattedEvent || '';
}

function shortenPlayerNames(originalText, players = []) {
	let newText = originalText;

	if (newText.includes('Stoppage')) {
		newText = newText
			.replace('Stoppage - ', '')
			.replace('-Home', '')
			.replace('-Away', '');
	}

	if (newText.includes('Coachs')) {
		newText = newText.replace('Coachs', "Coach's");
	}

	if (!players.length) {
		return newText;
	}

	for (const player of players) {
		const textToReplace = `${player.first_name} ${player.last_name}`;
		const playerInitial = player.first_name.charAt(0);
		const replacementText = `<b>${playerInitial}. ${player.last_name}</b>`;

		newText = newText.replace(textToReplace, replacementText);
	}

	return newText;
}

function getPlayColor(eventType) {
	const red = 'red';
	const grey = 'grey';
	const brightBlue = 'brightBlue';

	switch (eventType) {
		case 'awardedgoal':
		case 'emptynetgoal':
		case 'goal':
		case 'owngoal':
		case 'penaltygoal':
		case 'shootoutgoal':
		case 'shotsaved':
			return brightBlue;
		case 'hit':
		case 'penalty':
			return red;
		default:
			return grey;
	}
}

function getTeamLogo(teamID, homeTeam, visitingTeam) {
	return teamID === homeTeam.id
		? getSharedTeamLogo(homeTeam, true, 'image_url_90')
		: teamID === visitingTeam.id
		? getSharedTeamLogo(visitingTeam, true, 'image_url_90')
		: '';
}
function getTeamColor(teamID, homeTeam, visitingTeam) {
	return teamID === homeTeam.id ? homeTeam.color : teamID === visitingTeam.id ? visitingTeam.color : null;
}

export function formatInjuries(teamInjuries = []) {
	if (!teamInjuries) return [];
	const formattedInjuries = teamInjuries.map(injury => {
		return {
			playerName: getPlayerLink(injury),
			playerPosition: injury.position,
			type: `${injury.type.charAt(0).toUpperCase()}${injury.type.slice(1)}`,
			status: injury.status,
			timestamp: injury.timestamp,
			emptyColumn: ' ',
		};
	});

	return formattedInjuries.sort((a, b) => b.timestamp - a.timestamp);
}

export function formatSeasonLeaders(homeTeam, visitingTeam) {
	const leaderCategories = [
		{
			title: 'Points',
			categoryKey: 'point_leader',
			valueKey: 'total',
		},
		{
			title: 'Goals',
			categoryKey: 'goal_leader',
			valueKey: 'total',
		},
		{
			title: 'Assists',
			categoryKey: 'assist_leader',
			valueKey: 'total',
		},
		{
			title: 'Wins',
			categoryKey: 'win_leader',
			valueKey: 'total',
		},
	];

	const seasonLeaders = leaderCategories.map(leaderCategory => {
		const homeTeamData = homeTeam?.[leaderCategory.categoryKey];
		const visitingTeamData = visitingTeam?.[leaderCategory.categoryKey];

		if (!homeTeamData || !visitingTeamData) {
			return null;
		}

		let homeTeamValue = homeTeamData[leaderCategory.valueKey];
		let visitingTeamValue = visitingTeamData[leaderCategory.valueKey];

		if (leaderCategory.alternateValueKey) {
			homeTeamValue = homeTeamValue || homeTeamData[leaderCategory.alternateValueKey];
			visitingTeamValue = visitingTeamValue || visitingTeamData[leaderCategory.alternateValueKey];
		}

		if (leaderCategory.decimalPlaces) {
			homeTeamValue = homeTeamValue.toFixed(leaderCategory.decimalPlaces);
			visitingTeamValue = visitingTeamValue.toFixed(leaderCategory.decimalPlaces);
		}

		return {
			title: leaderCategory.title,
			homeTeamShortName: overrideTeamTricode(homeTeam.short_name),
			homeTeamColor: homeTeam.color,
			visitingTeamColor: visitingTeam.color,
			visitingTeamShortName: overrideTeamTricode(visitingTeam.short_name),
			homeTeamPlayerImage: homeTeamData.image_urls.xs,
			visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
			homeTeamLogo: homeTeam.image_url_90,
			visitingTeamLogo: visitingTeam.image_url_90,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
			visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${visitingTeamData.last_name}`,
			homeTeamDetails: {
				first_name: homeTeamData.first_name,
				last_name: homeTeamData.last_name,
				id: homeTeamData.id,
			},
			visitingTeamDetails: {
				first_name: visitingTeamData.first_name,
				last_name: visitingTeamData.last_name,
				id: visitingTeamData.id,
			},
		};
	});
	return seasonLeaders.filter(n => n); // filter out null values
}

export function formatTeamStats(homeTeam, visitingTeam) {
	if (!visitingTeam.season_stats || !homeTeam.season_stats) {
		return [];
	}

	const statCategories = [
		{
			title: 'Goals Per Game',
			valueKey: 'goals_per_game',
			valueFormatter: n => formatNumberWithDecimal(n, 2),
		},
		{
			title: 'Goals Against Per Game',
			valueFormatter: n => formatNumberWithDecimal(n, 2),
			valueKey: 'goals_against_per_game',
		},
		{
			title: 'Power Play Percentage',
			valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
			valueKey: 'power_play_percentage',
			isPercent: true,
		},
		{
			title: 'Penalty Kill Percentage',
			valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
			valueKey: 'penalty_kill',
			isPercent: true,
		},
	];

	const teamStats = statCategories.map(statCategory => {
		let homeTeamValue = homeTeam.season_stats[statCategory.valueKey];
		let visitingTeamValue = visitingTeam.season_stats[statCategory.valueKey];

		if ((!homeTeamValue && homeTeamValue !== 0) || (!visitingTeamValue && visitingTeamValue !== 0)) {
			return null;
		}

		return {
			title: statCategory.title,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			isPercent: statCategory.isPercent,
			valueFormatter: statCategory.valueFormatter,
		};
	});
	return teamStats.filter(n => n); // filter out null values
}

export function formatGameStats(homeTeam, visitingTeam) {
	if (!visitingTeam.game_stats || !homeTeam.game_stats) {
		return [];
	}

	let statCategories = [
		{
			title: 'Shots on Goal',
			valueKey: 'shots',
		},
		{
			title: 'Hits',
			valueKey: 'hits',
		},
		{
			title: 'Faceoffs Won',
			valueKey: 'faceoffs',
		},
		{
			title: 'Power Plays',
			valueKey: 'powerplay',
		},
		{
			title: 'Penalty Minutes',
			valueKey: 'penalties_in_minutes',
		},
		{
			title: 'Giveaways',
			valueKey: 'turnover_giveaways',
		},
		{
			title: 'Takeaways',
			valueKey: 'turnover_takeaways',
		},
		{
			title: 'Blocked Shots',
			valueKey: 'blocks',
		},
	];

	// Remove hits and penalty minutes for All-Star games
	if (homeTeam.name === 'All-Stars' || visitingTeam.name === 'All-Stars') {
		statCategories = statCategories.filter(stat => stat.title !== 'Hits' && stat.title !== 'Penalty Minutes');
	}

	const gameStats = statCategories.map(statCategory => {
		let homeTeamValue = homeTeam.game_stats[statCategory.valueKey];
		let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey];

		if ((!homeTeamValue && homeTeamValue !== 0) || (!visitingTeamValue && visitingTeamValue !== 0)) {
			return null;
		}

		return {
			title: statCategory.title,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			valueFormatter: statCategory.valueFormatter,
		};
	});
	return gameStats.filter(n => n); // filter out null values
}

export function formatSeasonRecord(team, isFournations = false) {
	if (isFournations && team?.team_standings?.overtime_wins >= 0) {
		return `${team.team_standings.wins}-${team.team_standings.overtime_wins}-${team.team_standings.overtime_losses}-${team.team_standings.losses}`;
	} else if (
		team?.team_standings?.wins >= 0 &&
		team?.team_standings?.losses >= 0 &&
		team?.team_standings?.overtime_losses >= 0
	) {
		return `${team.team_standings.wins}-${team.team_standings.losses}-${team.team_standings.overtime_losses}`;
	} else if (team?.team_standings?.wins >= 0 && team?.team_standings?.losses >= 0) {
		return `${team.team_standings.wins}-${team.team_standings.losses}`;
	} else {
		return '';
	}
}
export function formatShotCount(team) {
	if (team?.game_stats?.shots >= 0) {
		return `SOG: ${team.game_stats.shots}`;
	} else {
		return '';
	}
}

export function formatStength(homeTeam, visitingTeam) {
	if (
		homeTeam.strength !== visitingTeam.strength ||
		homeTeam.strength === 5 ||
		visitingTeam.strength === 5 ||
		homeTeam.strength === 0 ||
		visitingTeam.strength === 0
	) {
		return null;
	}

	return `${visitingTeam.strength} on ${homeTeam.strength}`;
}

export function sortByClockTime(a, b) {
	let aClockTime = parseFloat(a.split(':').join('.'));
	let bClockTime = parseFloat(b.split(':').join('.'));
	if (aClockTime < bClockTime) {
		return -1;
	}
	if (aClockTime > bClockTime) {
		return 1;
	}
	return 0;
}

export function formatBoxscore(team = {}, isFournations = false) {
	const { goalies = [], skaters = [] } = team;

	skaters?.sort((a, b) => (a.last_name > b.last_name ? 1 : -1));
	goalies?.sort((a, b) => (a.last_name > b.last_name ? 1 : -1));

	return [
		{
			title: 'Skaters',
			columns: [
				{ title: 'G', dataKey: 'goals', tooltipText: 'Goals' },
				{ title: 'A', dataKey: 'assists', tooltipText: 'Assists' },
				{ title: 'P', dataKey: 'points', tooltipText: 'Points' },
				{ title: '+/-', dataKey: 'plusMinusRatio', tooltipText: 'Plus Minus Ratio' },
				{ title: 'SOG', dataKey: 'shotsOnGoal', tooltipText: 'Shots on Goal' },
				{ title: 'PIM', dataKey: 'penaltyInMinutes', tooltipText: 'Penalty Minutes' },
				{
					title: 'TOI',
					dataKey: 'timeOnIce',
					tooltipText: 'Time on Ice',
					sortFunction: sortByClockTime,
				},
				{ title: 'H', dataKey: 'hits', tooltipText: 'Hits' },
				{ title: 'BLK', dataKey: 'blocks', tooltipText: 'Blocked Shots' },
			],
			data:
				skaters?.map(skater => {
					return {
						name: !isFournations
							? getPlayerLinkWithNumberAndPosition(skater)
							: getPlayerWithoutLinkWithNumberAndPosition(skater),
						goals: skater.goals,
						assists: skater.assists,
						points: skater.points,
						plusMinusRatio: skater.plus_minus_ratio,
						shotsOnGoal: skater.shots_on_goal,
						penaltyInMinutes: skater.penalty_in_minutes,
						hits: skater.hits,
						blocks: skater.blocks,
						timeOnIce: skater.time_on_ice,
						turnoverGiveaways: skater.turnover_giveaways,
						turnoverTakeaways: skater.turnover_takeaways,
						number: skater.number,
					};
				}) || [],
		},
		{
			title: 'Goalies',
			columns: [
				{ title: 'GA', dataKey: 'goalsAgainst', tooltipText: 'Goals Against' },
				{ title: 'SV', dataKey: 'saves', tooltipText: 'Saves' },
				{ title: 'SA', dataKey: 'shotsAgainst', tooltipText: 'Shots Against' },
				{ title: 'SV%', dataKey: 'savePercentage', tooltipText: 'Save Percentage' },
				{ title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes Played' },
				{},
				{},
				{},
				{},
			],
			data:
				goalies?.map(goalie => {
					return {
						name: !isFournations
							? getPlayerLinkWithNumberAndPosition(goalie)
							: getPlayerWithoutLinkWithNumberAndPosition(goalie),
						goalsAgainst: goalie.goals_against,
						saves: goalie.saves,
						shotsAgainst: goalie.shots_against,
						savePercentage: goalie.save_percentage && parseFloat(goalie.save_percentage).toFixed(3),
						minutes: goalie.minutes,
					};
				}) || [],
		},
	];
}
export function formatFaceoffs(team = {}, isFournations = false) {
	const { skaters = [] } = team;
	return [
		{
			title: 'Faceoffs',
			columns: [
				{ title: 'FW', dataKey: 'faceoffsWon', tooltipText: 'Faceoffs Won' },
				{ title: 'FL', dataKey: 'faceoffsLost', tooltipText: 'Faceoffs Lost' },
				{ title: 'FO%', dataKey: 'faceoffsPercent', tooltipText: 'Faceoff Win Percentage' },
				{},
				{},
				{},
				{},
				{},
				{},
			],
			data:
				skaters
					?.map(skater => {
						if (skater.faceoffs === 0 && skater.faceoff_lost === 0) return null;
						return {
							name: !isFournations
								? getPlayerLinkWithNumberAndPosition(skater)
								: getPlayerWithoutLinkWithNumberAndPosition(skater),
							faceoffsWon: skater.faceoffs,
							faceoffsLost: skater.faceoff_lost,
							faceoffsPercent: calculatePercentage(skater.faceoffs, skater.faceoffs + skater.faceoff_lost, 1),
						};
					})
					.filter(n => n) || [], // filter out null values
		},
	];
}

export function formatScratches(lineup = []) {
	if (!lineup) return '';
	const scratchedPlayers = lineup.filter(player => player.type === 'Bench');
	const formattedScratchedPlayers = scratchedPlayers.map(player => {
		return `${player.first_name} ${player.last_name}`;
	});
	return formattedScratchedPlayers.join(', ');
}

export function formatPeriods(periods, gameType, isAllStar) {
	const periodTotal = isAllStar ? 2 : 3;
	let defaultPeriods = [];
	for (let i = 0; i < periodTotal; i++) {
		defaultPeriods = [
			...defaultPeriods,
			{
				visitingTeamScore: '-',
				homeTeamScore: '-',
				label: '',
			},
		];
	}
	let filledPeriods = periods ? update(defaultPeriods, { $merge: periods }) : [];
	let labelledPeriods = labelPeriods(filledPeriods, gameType);

	return [filledPeriods, labelledPeriods];
}

export function isGameTypePostseason(gameType) {
	switch (gameType) {
		case 'Post Season':
			return true;
		default:
			return false;
	}
}

export function formatGamecardData(game = {}, options = {}) {
	const gameStatus = game.game_status;
	const {
		visitingTeamName,
		visitingTeamLogo,
		homeTeamName,
		homeTeamLogo,
		broadcastName,
		broadcastString,
		gameLoser,
		gameTime,
		gameDate,
	} = formatGameTeamDetails(game);

	const matchupResult = game.series_matchups_result?.toUpperCase();

	let powerplayTeam = 'hide';
	if (game.new_visiting_powerplay) powerplayTeam = 'visiting';
	if (game.new_home_powerplay) powerplayTeam = 'home';

	const commonProps = {
		visitingTeamName,
		visitingTeamLogo,
		homeTeamName,
		homeTeamLogo,
		link: preserveQueryParams(buildGamePageUrl(game)),
	};

	if (gameStatus === 'Pre-Game') {
		return {
			...commonProps,
			visitingTeamData: game.new_visiting_odds || '\u00A0',
			homeTeamData: game.new_home_odds || '\u00A0',
			teamDataStyle: 'odds',
			footerText: matchupResult,
			renderGameStatus: () => {
				return (
					<>
						{options.showDate ? (
							<div className='game_date'>
								{gameDate}, {gameTime}
							</div>
						) : (
							<div className='game_date'>{gameTime}</div>
						)}
						{broadcastName && <div className='broadcastName'>{broadcastName}</div>}
					</>
				);
			},
		};
	}
	if (gameStatus === 'In-Progress') {
		return {
			...commonProps,
			visitingTeamData: `${game.visiting_team?.score}`,
			homeTeamData: `${game.home_team?.score}`,
			powerplay: powerplayTeam,
			footerText: broadcastString,
			renderGameStatus: () => {
				const isOlympicLeague = ['oly_mhk', 'oly_whk'].includes(game.league);
        // For Olympics hockey use additional clock_data flag to show clock
				const showClockData = !isOlympicLeague || game.clock_data;

				return (
					<>
						{showClockData && <div>{game.clock}</div>}
						{game.new_status_long.split(' ').map((statusItem, index) => {
							return <div key={index}>{statusItem}</div>;
						})}
					</>
				);
			},
		};
	}
	if (gameStatus === 'Final') {
		return {
			...commonProps,
			visitingTeamData: `${game.visiting_team?.score}`,
			homeTeamData: `${game.home_team?.score}`,
			loser: gameLoser,
			footerText: matchupResult,
			renderGameStatus: () => <div>{game.new_status_long}</div>,
		};
	}

	if (gameStatus === 'if-necessary' || gameStatus === 'time-tbd') {
		return {
			...commonProps,
			visitingTeamData: '\u00A0',
			homeTeamData: '\u00A0',
			footerText: matchupResult,
			renderGameStatus: () => {
				return (
					<>
						<div>TBD</div>
					</>
				);
			},
		};
	}

	// For games that are cancelled, postponed, forfeit, etc...
	return {
		...commonProps,
		visitingTeamData: '\u00A0',
		homeTeamData: '\u00A0',
		footerText: matchupResult,
		renderGameStatus: () => {
			return (
				<>
					<div>{gameStatus}</div>
				</>
			);
		},
	};
}

export function formatDivisionRank(team) {
	if (team.division) {
		return `${formatDivisionRanksNumericValue(team.division.rank)} ${team.division.name}`;
	} else {
		return '';
	}
}

export function formatGoalieInfo(goalie) {
	if (goalie !== undefined) {
		let initial = goalie.first_name.charAt(0);
		let lastName = goalie.last_name;
		let wins = goalie.record.match(/W-(\d*)/)?.[1];
		let losses = goalie.record.match(/L-(\d*)/)?.[1];
		let overtimeLosses = goalie.record.match(/OTL-(\d*)/)?.[1];

		if (goalie && initial && lastName && wins === 0 && losses === 0 && overtimeLosses === 0) {
			return `${initial}. ${lastName}`;
		} else if (
			goalie &&
			initial &&
			lastName &&
			wins !== undefined &&
			losses !== undefined &&
			overtimeLosses !== undefined
		) {
			return `${initial}. ${lastName} (${wins}-${losses}-${overtimeLosses})`;
		} else if (goalie && initial && lastName && wins !== undefined && losses !== undefined) {
			return `${initial}. ${lastName} (${wins}-${losses})`;
		} else {
			return '';
		}
	}
	return '';
}
